import {CommonModule} from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  computed,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  ViewChild,
  viewChildren,
  ChangeDetectionStrategy,
} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import {Router} from '@angular/router';
import {LetDirective} from '@ngrx/component';
import {Actions, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {Image} from '@sportngin/se-commerce-sdk/models/clare';
import {combineLatest, map, Observable, Subject, take, takeUntil} from 'rxjs';
import {SeFeAlertModule} from 'se-fe-alert';
import {SeFeApiModule} from 'se-fe-api';
import {SeFeAttachmentServiceModule} from 'se-fe-attachment-service';
import {SeFeButtonModule} from 'se-fe-button';
import {SeFeCardModule} from 'se-fe-card';
import {SeFeChipModule} from 'se-fe-chip';
import {SeFeDomainEnvironmentModule} from 'se-fe-domain-environment';
import {SeFeFormFieldConfig, SeFeFormFieldModule} from 'se-fe-form-field';
import {
  SeFeFormFieldMultiselectComponent,
  SeFeFormFieldMultiselectModule,
  SeFeMultiselectOptions,
} from 'se-fe-form-field-multiselect';
import {SeFeFormFieldTextModule} from 'se-fe-form-field-text';
import {SeFeFormServiceModule} from 'se-fe-form-service';
import {SeFeGridModule} from 'se-fe-grid';
import {SeFeHeaderModule} from 'se-fe-header';
import {SeFeIconModule} from 'se-fe-icon';
import {SeFeMenuModule, SeFeMenuOptions} from 'se-fe-menu';
import {SeFePageModule} from 'se-fe-page';
import {SeFeTestIdModule} from 'se-fe-test-id';
import {SeFeToastService} from 'se-fe-toast';
import {SeFeWysiwygModule} from 'se-fe-wysiwyg';
import {ItemOrgCardComponent} from '../../../../common/components/item-org-card/item-org-card.component';
import {MultiImageUploadFieldComponent} from '../../../../common/components/multi-image-upload-field/multi-image-upload-field.component';
import {FullPageSpinnerComponent} from '../../../../common/components/full-page-spinner/full-page-spinner.component';
import {ProductManagementErrorInterface} from '../../../../error/error-handling/product-management-error';
import {CatalogSummary, getProductId, Product} from '../../../../models/clare';
import {AnalyticsEvents, AnalyticsService} from '../../../../services/analytics.service';
import {CreateBundleService} from '../../../../services/bundle/create-bundle.service';
import {selectOrganizationName} from '../../../../state/organization/organization.selectors';
import {selectSelectedOrganizationId} from '../../../../state/router/router.selectors';
import {trimFormValue} from '../../../../utils/form-utils';
import * as ImageFormUtils from '../../../../utils/image-form-utils';
import {ProductListActions} from '../../../product-list/store/product-list.actions';
import {BundleFormValue} from '../../types/create-bundle.types';
import {canBundleBeActive, hasActiveVariations} from '../../utils/bundle-activation.utils';
import {bundleFormValidator} from '../../utils/bundle-form.validators';
import {ProductsSelectedEvent} from '../included-products-card/choose-bundle-products-modal/choose-bundle-products-modal.component';
import {IncludedProductsCardComponent} from '../included-products-card/included-products-card.component';
import {CreateBundleActions} from './store/create-bundle.actions';
import {
  selectCreateBundleCatalogs,
  selectCreateBundleCatalogsLoading,
  selectCreateBundleError,
  selectCreateBundleLoading,
} from './store/create-bundle.selectors';

@Component({
  selector: 'create-bundle-component',
  standalone: true,
  imports: [
    CommonModule,
    FullPageSpinnerComponent,
    IncludedProductsCardComponent,
    ItemOrgCardComponent,
    LetDirective,
    MultiImageUploadFieldComponent,
    SeFeAlertModule,
    SeFeApiModule,
    SeFeAttachmentServiceModule,
    SeFeButtonModule,
    SeFeCardModule,
    SeFeChipModule,
    SeFeDomainEnvironmentModule,
    SeFeFormFieldModule,
    SeFeFormFieldMultiselectModule,
    SeFeFormFieldTextModule,
    SeFeFormServiceModule,
    SeFeGridModule,
    SeFeHeaderModule,
    SeFeIconModule,
    SeFeMenuModule,
    SeFePageModule,
    SeFeTestIdModule,
    SeFeWysiwygModule,
  ],
  templateUrl: './create-bundle.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './create-bundle.component.scss',
})
export class CreateBundleComponent implements OnInit, OnDestroy {
  @ViewChild(IncludedProductsCardComponent) includedProductsCard: IncludedProductsCardComponent;

  selectControls = viewChildren(SeFeFormFieldMultiselectComponent);

  bundleForm: FormGroup;
  ready = false;
  selectedProducts = signal<Product[]>([]);
  selectedItemsById = signal<Record<string, Product>>({});
  ineligibleProducts = signal<Product[]>([]);
  savePending = signal(false);
  bundlePriceManuallyEdited = false;
  readonly bundleImageAltValidators = [Validators.required, Validators.maxLength(200)];

  bundleStatusMenuOptions: SeFeMenuOptions = {
    name: 'Product bundle status',
    sections: [
      {
        menuItems: [
          {
            text: 'Active',
            action: () => this.setBundleStatus(true),
          },
          {
            text: 'Inactive',
            action: () => this.setBundleStatus(false),
          },
        ],
      },
    ],
  };

  bundleActive = signal<boolean>(false);

  bundleNameConfig: SeFeFormFieldConfig = {
    label: 'Name',
    errorMessages: {
      required: 'Please enter a bundle name',
      maxlength: 'Name exceeds maximum length of 100 characters.',
    },
  };

  bundleDescLabel: SeFeFormFieldConfig = {
    label: 'Description',
    required: false,
  };

  bundleImageLabel: SeFeFormFieldConfig = {
    label: 'Bundle Images',
    required: false,
  };

  bundleCatalogsLabel: SeFeFormFieldConfig = {
    label: 'Catalogs',
    required: false,
  };

  readonly onDestroy = new Subject<void>();

  error$: Observable<ProductManagementErrorInterface | undefined>;
  loading$: Observable<boolean>;
  loading: Signal<boolean>;
  catalogs: Signal<CatalogSummary[]>;
  catalogsLoading: Signal<boolean>;
  pageLoading: Signal<boolean>;
  catalogOptions: Signal<SeFeMultiselectOptions>;
  vm$: Observable<{
    backLink: {backLink: string; backLinkParams: Record<string, string>};
    product: {name: string};
  }>;

  orgId: Signal<string | undefined>;
  orgName: Signal<string | undefined>;

  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private store: Store,
    private actions$: Actions,
    private router: Router,
    private createBundleService: CreateBundleService,
    private toast: SeFeToastService,
    private analytics: AnalyticsService
  ) {
    this.loading$ = this.store.select(selectCreateBundleLoading);
    this.loading = toSignal(this.loading$, {initialValue: false});
    this.error$ = this.store.select(selectCreateBundleError);
    this.orgId = toSignal(this.store.select(selectSelectedOrganizationId), {requireSync: true});
    this.orgName = toSignal(this.store.select(selectOrganizationName), {requireSync: true});
    this.catalogs = toSignal(this.store.select(selectCreateBundleCatalogs), {requireSync: true});
    this.catalogsLoading = toSignal(this.store.select(selectCreateBundleCatalogsLoading), {requireSync: true});
    this.pageLoading = computed(() => this.loading() || this.catalogsLoading());
    this.catalogOptions = computed<SeFeMultiselectOptions>(() => ({
      sections: [
        {
          options: this.catalogs().map(catalog => ({text: catalog.name, value: String(catalog.id)})),
        },
      ],
    }));
    this.vm$ = combineLatest([this.loading$, this.store.select(selectSelectedOrganizationId)]).pipe(
      map(([, orgId]) => ({
        backLink: {
          backLink: `/org/${orgId ?? ''}/commerce/bundles`,
          backLinkParams: {},
        },
        product: {name: 'Create product bundle'},
      }))
    );
  }

  get descriptionControl(): FormControl<string> {
    return this.bundleForm.get('description') as FormControl;
  }

  get bundleNameControl(): FormControl<string> {
    return this.bundleForm.get('bundleName') as FormControl;
  }

  get imageDescriptionControl(): FormControl<string> {
    return this.bundleForm.get('imageDescription') as FormControl;
  }

  get bundleImagesFormArray(): FormArray<ImageFormUtils.ImageFormGroup> {
    return this.bundleForm.get('images') as FormArray<ImageFormUtils.ImageFormGroup>;
  }

  get bundleImages(): Image[] {
    return ImageFormUtils.mapImageFormArrayToImages(this.bundleImagesFormArray);
  }

  set bundleImages(images: Image[]) {
    if (!this.bundleForm) {
      return;
    }

    ImageFormUtils.replaceImageFormArray(
      this.bundleImagesFormArray,
      images,
      this.createBundleImageFormGroup.bind(this)
    );
    this.bundleImagesFormArray.updateValueAndValidity({emitEvent: false});
    this.bundleForm.updateValueAndValidity({emitEvent: false});
  }

  get bundlePriceControl(): FormControl<string> {
    return this.bundleForm.get('bundlePrice') as FormControl;
  }

  ngOnInit(): void {
    this.store.dispatch(CreateBundleActions.loadCatalogs());
    this.createForm();
    this.actions$
      .pipe(ofType(CreateBundleActions.createBundleSuccess), takeUntil(this.onDestroy))
      .subscribe(({bundleId}) => {
        this.savePending.set(false);
        const orgId = this.orgId();
        if (orgId && bundleId) {
          this.router.navigate(['/org', orgId, 'commerce', 'bundles', bundleId], {
            replaceUrl: true,
          });
        }
      });
    this.actions$.pipe(ofType(CreateBundleActions.createBundleFailed), takeUntil(this.onDestroy)).subscribe(() => {
      this.savePending.set(false);
    });
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  handleSave(): void {
    if (this.saveButtonDisabled()) {
      return;
    }

    // Check bundle price validation when products are selected
    if (
      this.selectedProducts().length > 0 &&
      this.includedProductsCard &&
      !this.includedProductsCard.bundlePriceForm.valid
    ) {
      this.includedProductsCard.bundlePriceForm.markAllAsTouched();
      this.includedProductsCard.bundlePriceForm.markAllAsDirty();
      this.includedProductsCard.bundlePriceForm.updateValueAndValidity();
      return;
    }

    this.savePending.set(true);

    // Defer touched/dirty/validity mutations to avoid Angular v21 NG0100 in dev mode.
    setTimeout(() => {
      this.trimFormValues();
      this.markFormGroupControls(this.bundleForm);
      this.validateAndSyncBundlePrice();
      this.cdr.markForCheck();
      if (this.bundleForm.valid) {
        this.dispatchCreateBundle();
      } else {
        this.savePending.set(false);
      }
    }, 0);
  }

  saveButtonDisabled(): boolean {
    return this.loading() || this.savePending() || !this.bundleNameControl?.value?.trim();
  }

  /** Navigates back to the bundle list without saving. */
  onCancel(): void {
    this.analytics.trackAction(AnalyticsEvents.CancelAddBundle, {event_type: 1});

    const orgId = this.orgId();
    if (orgId) {
      this.router.navigate(['/org', orgId, 'commerce', 'bundles']);
    }
  }

  /** Clears legacy image description validators after the user resets the uploader. */
  onBundleImagesManualClear(): void {
    setTimeout(() => {
      this.imageDescriptionControl.removeValidators(Validators.required);
      this.imageDescriptionControl.updateValueAndValidity({onlySelf: false, emitEvent: false});
      this.cdr.markForCheck();
    }, 0);
  }

  formHasErrors(): boolean {
    return this.bundleForm.invalid;
  }

  markFormGroupControls(formGroup: FormGroup | FormArray): void {
    Object.values(formGroup.controls).forEach(control => {
      if (control instanceof FormGroup || control instanceof FormArray) {
        this.markFormGroupControls(control);
      } else {
        control.markAsTouched({onlySelf: true});
        control.markAsDirty({onlySelf: true});
      }
    });
  }

  /**
   * Handles selection of products for the bundle.
   * - If no products are selected, clears the selection.
   * - If products are selected, ensures each has price data (fetches if missing).
   * - Merges fetched price data with existing products.
   * - Updates selectedProducts signal with the final product list.
   *
   * @param products Array of selected Product objects
   */
  onProductsSelected(event: ProductsSelectedEvent): void {
    const {products} = event;
    if (!products || products.length === 0) {
      this.handleEmptyProductSelection();
      return;
    }

    // Use service to fetch product details if needed
    this.createBundleService
      .hydrateSelectedProducts(products)
      .pipe(take(1))
      .subscribe({
        next: ({products: updatedProducts, itemsById, ineligibleProducts}) => {
          this.selectedProducts.set(updatedProducts);
          this.selectedItemsById.set(itemsById);
          this.ineligibleProducts.set(ineligibleProducts);
          this.syncBundleActivationState();
          this.cdr.markForCheck();
        },
        error: () => {
          // On error, use products as-is
          this.selectedProducts.set(products);
          this.selectedItemsById.set({});
          this.ineligibleProducts.set([]);
          this.syncBundleActivationState();
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Handles removal of a product from the bundle.
   * Updates the selectedProducts signal to exclude the removed product.
   * Updates bundle price with new product total if not manually edited.
   *
   * @param product The Product to remove from the bundle
   */
  onProductRemoved(product: Product): void {
    const productId = getProductId(product);
    this.selectedProducts.update((products: Product[]) => products.filter(p => getProductId(p) !== productId));
    this.selectedItemsById.update(itemsById => {
      if (!productId || !itemsById[productId]) {
        return itemsById;
      }

      const next = {...itemsById};
      delete next[productId];
      return next;
    });
    this.ineligibleProducts.update(items => items.filter(item => getProductId(item) !== productId));
    this.syncBundleActivationState();
    if (!this.bundlePriceManuallyEdited) {
      this.bundlePriceControl.setValue('', {emitEvent: true});
    }
  }

  /**
   * Handles bundle price changes from the included-products-card.
   * Marks the price as manually edited when user enters a value (non-empty).
   * Updates the bundlePrice form control with the new value.
   *
   * @param price The new bundle price value
   */
  onBundlePriceChanged(price: number): void {
    if (price > 0) {
      this.bundlePriceManuallyEdited = true;
    }
    this.bundlePriceControl.setValue(price.toString(), {emitEvent: true});
  }

  /**
   * Handles setting all products to active.
   * Dispatches NgRx actions to update products, which ensures the product list store stays in sync.
   * Listens for success actions to update the local selectedProducts signal.
   *
   * @param products Array of products to set to active
   */
  onSetAllProductsToActive(products: Product[]): void {
    if (!products || products.length === 0) {
      return;
    }

    const selectedItemsById = this.selectedItemsById();
    const ineligibleProducts = products
      .map(product => {
        const productId = getProductId(product);
        return productId ? selectedItemsById[productId] : undefined;
      })
      .filter((item): item is Product => item !== undefined && !hasActiveVariations(item));

    this.ineligibleProducts.set(ineligibleProducts);

    const productIds = products
      .filter(product => {
        const productId = getProductId(product);
        if (!productId) {
          return false;
        }

        const item = selectedItemsById[productId];
        return !item || hasActiveVariations(item);
      })
      .map(product => getProductId(product)!);

    if (productIds.length === 0) {
      this.cdr.markForCheck();
      return;
    }

    // Track how many products we're updating
    let completedCount = 0;
    const totalCount = productIds.length;

    // Listen for success actions to update local selectedProducts signal
    const subscription = this.actions$
      .pipe(
        ofType(ProductListActions.toggleProductActiveStatusSuccess),
        takeUntil(this.onDestroy),
        map(action => action.product)
      )
      .subscribe(updatedProduct => {
        // Update the selectedProducts signal with the updated product
        this.selectedProducts.update((currentProducts: Product[]) => {
          return currentProducts.map(currentProduct => {
            if (getProductId(currentProduct) === getProductId(updatedProduct)) {
              return updatedProduct;
            }
            return currentProduct;
          });
        });
        completedCount++;
        this.cdr.markForCheck();

        // Show success message when all updates are complete
        if (completedCount === totalCount) {
          this.toast.done('All products have been set to active');
          subscription.unsubscribe();
        }
      });

    // Dispatch actions for each product to update them to active
    // This ensures the product list store stays in sync
    productIds.forEach(productId => {
      this.store.dispatch(ProductListActions.toggleProductActiveStatus({productId, active: true}));
    });
  }

  setBundleStatus(isActive: boolean): void {
    if (isActive && !this.canActivateBundle()) {
      return;
    }
    this.bundleActive.set(isActive);
  }

  isBundleStatusDisabled(): boolean {
    return !this.canActivateBundle() && !this.bundleActive();
  }

  canActivateBundle(): boolean {
    return canBundleBeActive(this.bundleNameControl.value, this.selectedProducts());
  }

  getStatusChipText(isActive: boolean): string {
    return isActive ? 'Active' : 'Inactive';
  }

  getStatusChipStyle(isActive: boolean): string {
    return isActive ? 'done' : 'off';
  }

  createForm(): void {
    this.bundleForm = this.fb.group(
      {
        bundleName: new FormControl('', {
          validators: [Validators.required, Validators.maxLength(100)],
          nonNullable: true,
        }),
        catalogIds: new FormControl<string[]>([], {nonNullable: true}),
        bundleID: new FormControl('', {
          validators: [Validators.maxLength(100), Validators.pattern(/^[A-Z0-9]*$/)],
          nonNullable: true,
        }),
        bundlePrice: new FormControl('', {
          validators: [
            (control: AbstractControl): ValidationErrors | null => {
              const value = control.value;
              if (!value || value === '') {
                return null;
              }
              const patternError = Validators.pattern(/^\d+(\.\d{1,2})?$/)(control);
              if (patternError) {
                return patternError;
              }
              const numValue = parseFloat(value);
              if (isNaN(numValue) || numValue < 0) {
                return {min: {min: 0, actual: numValue}};
              }
              // $0 is valid (optional price); if entered, must be >= $0.01
              if (numValue > 0 && numValue < 0.01) {
                return {min: {min: 0.01, actual: numValue}};
              }
              return null;
            },
          ],
          nonNullable: true,
        }),
        imageDescription: ['', [Validators.maxLength(200)]],
        images: new FormArray<ImageFormUtils.ImageFormGroup>([]),
        description: ['', [Validators.maxLength(2000)]],
        bundleImage: [null],
        fulfilledBy: [this.orgName()],
      },
      {validators: bundleFormValidator()}
    );

    this.bundleNameControl.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => this.syncBundleActivationState());
    this.syncBundleActivationState();

    this.ready = true;
  }

  /**
   * Handles the case when no products are selected.
   * Clears the selected products and resets the bundle price if not manually edited.
   */
  private handleEmptyProductSelection(): void {
    this.selectedProducts.set([]);
    this.selectedItemsById.set({});
    this.ineligibleProducts.set([]);
    this.syncBundleActivationState();
    if (!this.bundlePriceManuallyEdited) {
      this.bundlePriceControl.setValue('', {emitEvent: true});
    }
  }

  private syncBundleActivationState(): void {
    if (!this.canActivateBundle()) {
      this.bundleActive.set(false);
    }
  }

  /**
   * Trims whitespace from form values that should be trimmed.
   */
  private trimFormValues(): void {
    trimFormValue(this.bundleForm, 'bundleName');
    trimFormValue(this.bundleForm, 'bundleID');
    trimFormValue(this.bundleForm, 'imageDescription');
    this.trimBundleImageDescriptions();
  }

  private createBundleImageFormGroup(src = '', alt = ''): ImageFormUtils.ImageFormGroup {
    return ImageFormUtils.createImageFormGroup(src, alt, [Validators.required, Validators.maxLength(200)]);
  }

  private trimBundleImageDescriptions(): void {
    ImageFormUtils.trimImageAltValues(this.bundleImagesFormArray);
  }

  /**
   * Validates and syncs bundle price between child and parent form controls.
   * Marks controls as touched/dirty and scrolls to invalid price input if needed.
   */
  private validateAndSyncBundlePrice(): void {
    this.syncChildBundlePriceToParent();
    this.markParentBundlePriceAsTouched();
    this.scrollToInvalidBundlePriceIfNeeded();
  }

  /**
   * Syncs the bundle price value from child form control to parent form control.
   * Marks child control as touched/dirty and triggers change detection.
   */
  private syncChildBundlePriceToParent(): void {
    if (!this.includedProductsCard?.bundlePriceForm) {
      return;
    }

    const childBundlePriceControl = this.includedProductsCard.bundlePriceForm.get('bundlePrice');
    if (!childBundlePriceControl) {
      return;
    }

    const childValue = childBundlePriceControl.value;
    if (childValue !== this.bundlePriceControl.value) {
      this.bundlePriceControl.setValue(childValue || '', {emitEvent: false});
    }

    childBundlePriceControl.markAsTouched({onlySelf: true});
    childBundlePriceControl.markAsDirty({onlySelf: true});
    childBundlePriceControl.updateValueAndValidity({onlySelf: true});

    this.includedProductsCard.cdr.markForCheck();
  }

  /**
   * Marks the parent bundle price control as touched/dirty to show validation errors.
   */
  private markParentBundlePriceAsTouched(): void {
    this.bundlePriceControl.markAsTouched({onlySelf: true});
    this.bundlePriceControl.markAsDirty({onlySelf: true});
    this.bundlePriceControl.updateValueAndValidity({onlySelf: true});
  }

  /**
   * Scrolls to and focuses the bundle price input if it's invalid.
   */
  private scrollToInvalidBundlePriceIfNeeded(): void {
    if (!this.bundlePriceControl.invalid) {
      return;
    }

    setTimeout(() => {
      const bundlePriceInput = document.getElementById('bundle-price-input');
      if (bundlePriceInput) {
        bundlePriceInput.scrollIntoView({behavior: 'smooth', block: 'center'});
        bundlePriceInput.focus();
      }
    }, 100);
  }

  /**
   * Dispatches the create bundle action with the form data.
   */
  private dispatchCreateBundle(): void {
    const values: BundleFormValue = this.bundleForm.value;
    const productIds = this.selectedProducts()
      .map(product => Number(product.id))
      .filter(productId => Number.isFinite(productId));

    this.store.dispatch(
      CreateBundleActions.createBundle({
        form: values,
        active: this.bundleActive() && this.canActivateBundle(),
        images: this.bundleImages.length ? this.bundleImages : undefined,
        productIds,
      })
    );
  }
}
